import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCreditCard, FiExternalLink, FiRefreshCw, FiX } from 'react-icons/fi';
import { billingPost, notifyBillingUpdated } from './billingApi';
import useBillingWallet from './useBillingWallet';
import fnc from '../mymod/commFunction';
import './WalletPage.scss';

const credit = value => `${Number(value || 0).toLocaleString()}C`;
const chargeUrl = 'https://kuebs.com/';
function getNativeWebsiteOpener() {
  const ios = window.webkit?.messageHandlers?.callWebSite;
  if (typeof ios?.postMessage === 'function') return url => ios.postMessage(url);
  const android = window.myJSInterface;
  if (typeof android?.callWebSite === 'function') return url => android.callWebSite(url);
  return null;
}
const labels = { omr_extract: '답안지 판독', omr_grade: '답안지 채점', essay_assess: '서술형 첨삭', scheduler_plan: '학습 계획', ai_study: 'AI 질문', reserved: '처리 중', completed: '완료', released: '예약 해제', failed: '실패', grant: '충전', manual_topup: '관리자 충전', reserve: '사용 예약', capture: 'AI 이용', release: '예약 해제', refund: '결제 취소', manual: '관리자 조정' };

export default function WalletPage({ embedded = false }) {
  const navigate = useNavigate();
  const { data, loading: busy, error: loadError, requiresLogin, balance, refresh } = useBillingWallet();
  const [error, setError] = useState('');
  const [chargeError, setChargeError] = useState('');
  const [job, setJob] = useState(null);
  const member = useRef({ token: fnc.getCookie('appSessionToken') });
  const syncMember = useCallback(() => {
    const token = fnc.getCookie('appSessionToken');
    if (member.current.token !== token) {
      member.current = { token };
      setJob(null);
      setError('');
    }
    return member.current;
  }, []);
  useEffect(() => {
    const changed = () => syncMember();
    const visible = () => { if (document.visibilityState === 'visible') syncMember(); };
    window.addEventListener('billing:updated', changed);
    window.addEventListener('storage', changed);
    window.addEventListener('focus', changed);
    window.addEventListener('pageshow', changed);
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.removeEventListener('billing:updated', changed);
      window.removeEventListener('storage', changed);
      window.removeEventListener('focus', changed);
      window.removeEventListener('pageshow', changed);
      document.removeEventListener('visibilitychange', visible);
      member.current = { token: '' };
    };
  }, [syncMember]);
  const isCurrentMember = (session) => session === member.current && session.token === fnc.getCookie('appSessionToken');
  const inspectJob = async requestId => {
    const session = syncMember();
    setError('');
    try {
      const result = await billingPost('job', { requestId, includeResult: false });
      if (isCurrentMember(session)) {
        setJob({ status: result.status, chargedCredits: result.chargedCredits });
        notifyBillingUpdated();
      }
    }
    catch (err) { if (isCurrentMember(session)) setError(err.message); }
  };
  const wallet = data?.wallet || data || {};
  const ledger = data?.ledger || data?.transactions || [];
  const ledgerRows = ledger.map((entry, index) => ({
    key: entry.id || index,
    date: entry.createdAt ? new Date(entry.createdAt).toLocaleString('ko-KR') : '-',
    type: entry.type === 'adjust' ? '관리자 조정' : labels[entry.type] || entry.type || entry.kind,
    balanceChange: credit(Number(entry.paidDelta || 0) + Number(entry.freeDelta || 0)),
    charged: entry.type === 'capture' ? credit(entry.chargedCredits) : '—',
    description: entry.reason || entry.note || labels[entry.featureCode] || entry.featureCode || '-',
  }));
  const jobs = data?.jobs || [];
  const openChargeHomepage = event => {
    setChargeError('');
    const openNative = getNativeWebsiteOpener();
    if (!openNative) return;
    // The native bridge handles navigation; do not also open the anchor's new tab.
    event.preventDefault();
    try {
      openNative(chargeUrl);
    } catch {
      setChargeError('충전 화면을 열지 못했습니다. 잠시 후 다시 시도해 주세요.');
    }
  };

  const Page = embedded ? 'section' : 'main';

  return <Page className={`personal-wallet${embedded ? ' personal-wallet--embedded' : ''}`} aria-label={embedded ? '내 AI 지갑' : undefined}>
    <header><div>{!embedded && <small>AI K-EDU</small>}<h1>{embedded ? '내 AI 지갑 · 충전' : '내 AI 지갑'}</h1></div>{!embedded && <button onClick={() => navigate('/menu')} aria-label="서비스 메뉴로 이동" title="닫기"><FiX /></button>}</header>
    <p>선생님과 학생 모두 본인이 실행한 AI 작업에 본인의 크레딧을 사용합니다.</p>
    <button className="wallet-refresh" onClick={refresh} disabled={busy}><FiRefreshCw /> {busy ? '불러오는 중…' : '잔액 새로고침'}</button>
    {(error || loadError) && <p role="alert" className="wallet-error">{error || loadError}</p>}
    {requiresLogin && <p>다시 로그인한 뒤 본인의 지갑을 확인해 주세요. <button type="button" onClick={() => navigate('/login')}>로그인</button></p>}
    {data?.enabled === false ? <p role="status">크레딧 서비스를 준비 중입니다. 잠시 후 다시 확인해 주세요.</p> : data && <>
      <section className="wallet-balance" aria-label="내 크레딧 잔액"><FiCreditCard /><span>사용 가능한 크레딧</span><strong>{busy ? '확인 중…' : credit(balance)}</strong><p>유료 {credit(wallet.paidCredits ?? wallet.paid)} · 무료 {credit(wallet.freeCredits ?? wallet.free)} · 사용 예약 {credit(wallet.reservedCredits)}</p></section>
      <section className="wallet-charge">
        <h2>크레딧 충전</h2>
        <p>홈페이지의 ‘충전하기’ 메뉴에서 이용권을 선택하고 토스로 충전해 주세요.</p>
        <a className="wallet-charge-link" href={chargeUrl} target="_blank" rel="noopener noreferrer" onClick={openChargeHomepage}>
          충전하기 <FiExternalLink aria-hidden="true" />
          <span className="wallet-charge-new-tab">{getNativeWebsiteOpener() ? '외부 브라우저' : '새 탭'}</span>
        </a>
        {chargeError && <p role="alert" className="wallet-error">{chargeError}</p>}
        <p className="wallet-charge-help">TALK와 같은 계정으로 로그인해 주세요. 충전 후 돌아오면 잔액이 갱신됩니다.</p>
      </section>
      <section>
        <h2>충전·사용 내역</h2>
        <p>작업을 시작하면 예상 금액을 예약합니다. 완료 후 남은 예약액은 사용 가능 잔액으로 돌아오며, 실제 사용한 크레딧은 별도로 표시됩니다.</p>
        {!ledgerRows.length ? <p>아직 거래 내역이 없습니다.</p> : <>
          <div className="wallet-table-wrap wallet-ledger-table"><table aria-label="충전·사용 내역">
            <thead><tr><th scope="col">일시</th><th scope="col">구분</th><th scope="col">사용 가능 잔액 증감</th><th scope="col">실제 사용</th><th scope="col">내용</th></tr></thead>
            <tbody>{ledgerRows.map(entry => <tr key={entry.key}>
              <td>{entry.date}</td>
              <td>{entry.type}</td>
              <td>{entry.balanceChange}</td>
              <td>{entry.charged}</td>
              <td>{entry.description}</td>
            </tr>)}</tbody>
          </table></div>
          <ul className="wallet-ledger-cards" aria-label="충전·사용 내역">
            {ledgerRows.map(entry => <li className="wallet-ledger-card" key={entry.key}>
              <div className="wallet-ledger-card__heading">
                <strong className="wallet-ledger-card__type">{entry.type}</strong>
                <span className="wallet-ledger-card__date">{entry.date}</span>
              </div>
              <dl>
                <div><dt>사용 가능 잔액 증감</dt><dd>{entry.balanceChange}</dd></div>
                <div><dt>실제 사용</dt><dd>{entry.charged}</dd></div>
                <div className="wallet-ledger-card__description"><dt>내용</dt><dd>{entry.description}</dd></div>
              </dl>
            </li>)}
          </ul>
        </>}
      </section>
      <section>
        <h2>AI 작업 확인</h2>
        <p>응답이 끊긴 작업은 다시 실행하기 전에 처리 상태와 차감된 크레딧을 확인해 주세요.</p>
        {jobs.length ? jobs.map(item => <button className="wallet-job" key={item.requestId} onClick={() => inspectJob(item.requestId)}>{labels[item.featureCode] || 'AI 작업'} · {labels[item.status] || '진행 상태 확인'}</button>) : <p>확인할 작업이 없습니다.</p>}
        {job && <div role="status" className="wallet-job-result"><strong>작업 상태: {labels[job.status] || '확인 중'}</strong><p>차감: {credit(job.chargedCredits)}</p></div>}
      </section>
    </>}
  </Page>;
}
